import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import type { TripPosition, TripStatus } from '../api'
import {
  formatDistance,
  formatSpeed,
  readPreferredMapType,
  storePreferredMapType,
  type UnitSystem,
} from '../preferences'
import { tripPointMetricsAt } from './tripPointMetrics'
import { focusedRouteZoom, maximumInitialRouteZoom, routeFitsAtZoom } from './tripViewport'
import { formatPositionAge } from './positionAge'
import {
  LIVE_ROUTE_ANIMATION_DURATION_MS,
  ROUTE_ANIMATION_DURATION_MS,
  createTripTimeline,
  formatClock,
  formatElapsed,
} from './tripTimeline'
import { distanceBetweenCoordinates, formatViewerDistance } from './viewerPosition'
import { configureGoogleMaps, importLibrary } from './googleMapsLoader'
import { LIVE_TAB_REFRESH_GRACE_MS, shouldCatchUpAfterTabHidden } from './liveVisibility'
import type { MapViewport } from './mapViewport'

const COMPLETED_ROUTE_COLOR = '#0000FF'
const LIVE_ROUTE_COLOR = '#c6283d'

interface ComparisonRoute {
  id: string
  positions: TripPosition[]
  status: TripStatus
  color: string
  replayRun: number
}

interface ComparisonPolylineState {
  polyline: google.maps.Polyline
  positionCount: number
  targetPositionCount: number
  presentedTime: number | null
  replayRun: number
  primary: boolean
}

function routeMarkerIcons(
  symbolPath: typeof google.maps.SymbolPath,
  status: TripStatus,
  color: string,
  startColor: string,
  completedEnd: boolean,
): google.maps.IconSequence[] {
  const startIcons: google.maps.IconSequence[] = [
    {
      fixedRotation: true,
      icon: {
        path: 'M 0,1 -8,-6 -8,-20 Q -8,-23 -5,-23 H 5 Q 8,-23 8,-20 V -6 Z',
        fillColor: '#ffffff',
        fillOpacity: 1,
        scale: 1.15,
        strokeColor: startColor,
        strokeWeight: 2.4,
      },
      offset: '0%',
    },
    {
      fixedRotation: true,
      icon: {
        path: 'M -6.7,-18.5 H 6.7 V -9 H -6.7 Z',
        fillColor: startColor,
        fillOpacity: 1,
        scale: 1.15,
        strokeColor: startColor,
        strokeWeight: 1,
      },
      offset: '0%',
    },
    {
      fixedRotation: true,
      icon: {
        path: 'M -4.8,-16.8 H -6.2 V -14.5 H -4.8 V -12.2 H -6.2 M -3.9,-16.8 H -1.9 M -2.9,-16.8 V -12.2 M -1.2,-12.2 0,-16.8 1.2,-12.2 M -.7,-14 H .7 M 2,-12.2 V -16.8 H 3.7 V -14.4 H 2 M 3.2,-14.4 4,-12.2 M 4.5,-16.8 H 6.5 M 5.5,-16.8 V -12.2',
        fillOpacity: 0,
        scale: 1.15,
        strokeColor: '#ffffff',
        strokeWeight: .85,
      },
      offset: '0%',
    },
    {
      fixedRotation: true,
      icon: {
        path: 'M 0,-2 -3.2,-7 H 3.2 Z',
        fillColor: startColor,
        fillOpacity: 1,
        scale: 1.15,
        strokeColor: startColor,
        strokeWeight: 1,
      },
      offset: '0%',
    },
  ]

  const endIcons: google.maps.IconSequence[] = status === 'ended' && completedEnd
    ? [
        {
          fixedRotation: true,
          icon: {
            path: 'M 0,1 -8,-6 -8,-20 Q -8,-23 -5,-23 H 5 Q 8,-23 8,-20 V -6 Z',
            fillColor: '#ffffff',
            fillOpacity: 1,
            scale: 1.15,
            strokeColor: color,
            strokeWeight: 2.4,
          },
          offset: '100%',
        },
        {
          fixedRotation: true,
          icon: {
            path: 'M -5.5,-18.5 H 6 V -9.5 H -5.5 Z',
            fillColor: color,
            fillOpacity: 1,
            scale: 1.15,
            strokeColor: color,
            strokeWeight: 1,
          },
          offset: '100%',
        },
        {
          fixedRotation: true,
          icon: {
            path: 'M -5.5,-18.5 H -1.7 V -15.5 H -5.5 Z M 2.2,-18.5 H 6 V -15.5 H 2.2 Z M -1.7,-15.5 H 2.2 V -12.5 H -1.7 Z M -5.5,-12.5 H -1.7 V -9.5 H -5.5 Z M 2.2,-12.5 H 6 V -9.5 H 2.2 Z',
            fillColor: '#ffffff',
            fillOpacity: 1,
            scale: 1.15,
            strokeOpacity: 0,
          },
          offset: '100%',
        },
        {
          fixedRotation: true,
          icon: {
            path: 'M -5.5,-19 V -7',
            fillOpacity: 0,
            scale: 1.15,
            strokeColor: color,
            strokeWeight: 1.5,
          },
          offset: '100%',
        },
      ]
    : [
        {
          icon: {
            path: symbolPath.CIRCLE,
            fillColor: '#ffffff',
            fillOpacity: 1,
            scale: 8,
            strokeColor: color,
            strokeWeight: 3,
          },
          offset: '100%',
        },
        {
          fixedRotation: false,
          icon: {
            path: symbolPath.FORWARD_CLOSED_ARROW,
            fillColor: color,
            fillOpacity: 1,
            scale: 4,
            strokeColor: color,
            strokeWeight: 1,
          },
          offset: '100%',
        },
      ]

  return [...startIcons, ...endIcons]
}

function routePathAtProgress(positions: TripPosition[], progress: number): google.maps.LatLngLiteral[] {
  const routePath = positions.map(({ latitude: lat, longitude: lng }) => ({ lat, lng }))
  const sample = createTripTimeline(positions).positionSampleAt(progress)
  if (sample.index < 0) return []

  const visiblePath = routePath.slice(0, sample.index + 1)
  if (sample.nextIndex !== null && sample.fraction > 0) {
    const from = routePath[sample.index]!
    const to = routePath[sample.nextIndex]!
    visiblePath.push({
      lat: from.lat + (to.lat - from.lat) * sample.fraction,
      lng: from.lng + (to.lng - from.lng) * sample.fraction,
    })
  }
  return visiblePath
}

function fitRouteToViewport(
  map: google.maps.Map,
  element: HTMLDivElement,
  positions: TripPosition[],
  path: google.maps.LatLngLiteral[],
): google.maps.MapsEventListener | null {
  if (positions.length === 0 || path.length === 0) return null
  const bounds = new google.maps.LatLngBounds()
  path.forEach((point) => bounds.extend(point))
  const maximumOverviewZoom = maximumInitialRouteZoom(positions)
  const listener = map.addListener('idle', () => {
    listener.remove()
    const automaticZoom = map.getZoom()
    if (automaticZoom === undefined) return

    let targetZoom = maximumOverviewZoom === undefined
      ? automaticZoom
      : Math.min(automaticZoom, maximumOverviewZoom)

    if (maximumOverviewZoom === undefined) {
      while (
        targetZoom < 21
        && routeFitsAtZoom(positions, targetZoom + 1, element.clientWidth, element.clientHeight)
      ) {
        targetZoom += 1
      }
    }

    if (targetZoom !== automaticZoom) map.setZoom(targetZoom)
  })
  map.fitBounds(bounds, 36)
  return listener
}

interface RouteFocusOverlay {
  remove(): void
  setInfo(text: string): void
  setPosition(position: google.maps.LatLngLiteral): void
}

interface ViewerPositionOverlay {
  remove(): void
  setInfo(text: string): void
  setPosition(position: google.maps.LatLngLiteral): void
}

interface PositionCommentOverlay {
  remove(): void
  setContent(comment: string, recordedAt: string): void
  setPosition(position: google.maps.LatLngLiteral): void
  setVisible(visible: boolean): void
}

interface IndexedPositionCommentOverlay {
  index: number
  overlay: PositionCommentOverlay
}

function positionCommentKey(routeId: string, position: TripPosition, index: number): string {
  const positionKey = position.id
    || `${position.recordedAt}:${position.latitude}:${position.longitude}:${index}`
  return `${routeId}:${positionKey}`
}

function setRouteCommentVisibility(
  overlaysByRoute: Map<string, IndexedPositionCommentOverlay[]>,
  routeId: string,
  visiblePositionIndex: number,
): void {
  overlaysByRoute.get(routeId)?.forEach(({ index, overlay }) => {
    overlay.setVisible(index <= visiblePositionIndex)
  })
}

function formatPositionCommentTime(value: string): string {
  if (!value) return ''
  const normalized = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`
  const date = new Date(normalized)
  return Number.isNaN(date.valueOf())
    ? value
    : new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

function createPositionCommentOverlay(
  OverlayView: typeof google.maps.OverlayView,
  map: google.maps.Map,
  initialPosition: google.maps.LatLngLiteral,
  initialComment: string,
  initialRecordedAt: string,
): PositionCommentOverlay {
  let position = new google.maps.LatLng(initialPosition)
  let comment = initialComment
  let recordedAt = initialRecordedAt
  let button: HTMLButtonElement | null = null
  let popup: HTMLSpanElement | null = null
  let message: HTMLSpanElement | null = null
  let time: HTMLTimeElement | null = null
  let expanded = false
  let visible = true
  const overlay = new OverlayView()

  const update = () => {
    if (!button || !popup || !message || !time) return
    button.hidden = !visible
    button.setAttribute('aria-expanded', String(expanded))
    button.title = expanded ? 'Hide position comment' : 'Read position comment'
    popup.hidden = !expanded
    message.textContent = comment
    time.textContent = formatPositionCommentTime(recordedAt)
    time.dateTime = recordedAt
    time.hidden = !recordedAt
  }
  const toggle = (event: MouseEvent) => {
    event.stopPropagation()
    expanded = !expanded
    update()
  }

  overlay.onAdd = () => {
    button = document.createElement('button')
    button.type = 'button'
    button.className = 'trip-map-comment'
    button.setAttribute('aria-label', 'Read position comment')
    Object.assign(button.style, {
      position: 'absolute',
      transform: 'translate(-50%, -100%)',
    })
    const icon = document.createElement('span')
    icon.className = 'trip-map-comment__icon'
    icon.setAttribute('aria-hidden', 'true')
    popup = document.createElement('span')
    popup.className = 'trip-map-comment__text'
    message = document.createElement('span')
    message.className = 'trip-map-comment__message'
    time = document.createElement('time')
    time.className = 'trip-map-comment__time'
    popup.append(message, time)
    button.append(icon, popup)
    button.addEventListener('click', toggle)
    update()
    overlay.getPanes()?.overlayMouseTarget.appendChild(button)
  }
  overlay.draw = () => {
    if (!button) return
    const point = overlay.getProjection().fromLatLngToDivPixel(position)
    if (!point) return
    button.style.left = `${point.x}px`
    button.style.top = `${point.y - 7}px`
  }
  overlay.onRemove = () => {
    button?.removeEventListener('click', toggle)
    button?.remove()
    button = null
    popup = null
    message = null
    time = null
  }
  overlay.setMap(map)

  return {
    remove: () => overlay.setMap(null),
    setContent: (nextComment, nextRecordedAt) => {
      comment = nextComment
      recordedAt = nextRecordedAt
      update()
    },
    setPosition: (nextPosition) => {
      position = new google.maps.LatLng(nextPosition)
      overlay.draw()
    },
    setVisible: (nextVisible) => {
      visible = nextVisible
      if (!visible) expanded = false
      update()
    },
  }
}

function createViewerPositionOverlay(
  OverlayView: typeof google.maps.OverlayView,
  map: google.maps.Map,
  initialPosition: google.maps.LatLngLiteral,
): ViewerPositionOverlay {
  let position = new google.maps.LatLng(initialPosition)
  let element: HTMLDivElement | null = null
  let info: HTMLSpanElement | null = null
  let infoText = ''
  const overlay = new OverlayView()

  overlay.onAdd = () => {
    element = document.createElement('div')
    element.className = 'trip-map-viewer-position'
    Object.assign(element.style, {
      position: 'absolute',
      transform: 'translate(-50%, -100%)',
    })
    info = document.createElement('span')
    info.className = 'trip-map-viewer-distance'
    info.textContent = infoText
    const marker = document.createElement('span')
    marker.className = 'trip-map-viewer-marker'
    marker.setAttribute('aria-hidden', 'true')
    element.append(info, marker)
    overlay.getPanes()?.overlayMouseTarget.appendChild(element)
  }
  overlay.draw = () => {
    if (!element) return
    const point = overlay.getProjection().fromLatLngToDivPixel(position)
    if (!point) return
    element.style.left = `${point.x}px`
    element.style.top = `${point.y}px`
  }
  overlay.onRemove = () => {
    element?.remove()
    element = null
    info = null
  }
  overlay.setMap(map)

  return {
    remove: () => overlay.setMap(null),
    setInfo: (text) => {
      infoText = text
      if (info) info.textContent = text
    },
    setPosition: (nextPosition) => {
      position = new google.maps.LatLng(nextPosition)
      overlay.draw()
    },
  }
}

function createRouteFocusOverlay(
  OverlayView: typeof google.maps.OverlayView,
  map: google.maps.Map,
  initialPosition: google.maps.LatLngLiteral,
  onActivate: () => void,
): RouteFocusOverlay {
  let position = new google.maps.LatLng(initialPosition)
  let button: HTMLButtonElement | null = null
  let info: HTMLSpanElement | null = null
  let infoText = ''
  const overlay = new OverlayView()

  overlay.onAdd = () => {
    button = document.createElement('button')
    button.type = 'button'
    button.className = 'trip-map-focus-target'
    button.setAttribute('aria-label', 'Zoom to trip route')
    button.title = 'Zoom to trip route'
    Object.assign(button.style, {
      position: 'absolute',
      transform: 'translate(-50%, -50%)',
    })
    info = document.createElement('span')
    info.className = 'trip-map-position-info'
    info.textContent = infoText
    button.appendChild(info)
    button.addEventListener('click', onActivate)
    overlay.getPanes()?.overlayMouseTarget.appendChild(button)
  }
  overlay.draw = () => {
    if (!button) return
    const point = overlay.getProjection().fromLatLngToDivPixel(position)
    if (!point) return
    button.style.left = `${point.x}px`
    button.style.top = `${point.y}px`
  }
  overlay.onRemove = () => {
    button?.removeEventListener('click', onActivate)
    button?.remove()
    button = null
    info = null
  }
  overlay.setMap(map)

  return {
    remove: () => overlay.setMap(null),
    setInfo: (text) => {
      infoText = text
      if (info) info.textContent = text
    },
    setPosition: (nextPosition) => {
      position = new google.maps.LatLng(nextPosition)
      overlay.draw()
    },
  }
}

export function GoogleTripMap({
  positions,
  status,
  unitSystem,
  followLatestPosition,
  showLivePositionInfo,
  showViewerPosition = false,
  progress,
  onProgressChange,
  onViewingLiveChange,
  timelineInfoSlot,
  timelineSideSlot,
  fitAvailableHeight = false,
  comparisonMode = false,
  comparisonRoutes = [],
  comparisonOverlaySlot,
  onViewportChange,
}: {
  positions: TripPosition[]
  status: TripStatus
  unitSystem: UnitSystem
  followLatestPosition: boolean
  showLivePositionInfo: boolean
  showViewerPosition?: boolean
  progress: number
  onProgressChange: (progress: number) => void
  onViewingLiveChange?: (viewingLive: boolean) => void
  timelineInfoSlot?: ReactNode
  timelineSideSlot?: ReactNode
  fitAvailableHeight?: boolean
  comparisonMode?: boolean
  comparisonRoutes?: ComparisonRoute[]
  comparisonOverlaySlot?: ReactNode
  onViewportChange?: (viewport: MapViewport) => void
}) {
  const elementRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const polylineRef = useRef<google.maps.Polyline | null>(null)
  const comparisonPolylinesRef = useRef(new Map<string, ComparisonPolylineState>())
  const comparisonAnimationFramesRef = useRef(new Map<string, number>())
  const fittedComparisonIdsRef = useRef('')
  const focusOverlayRef = useRef<RouteFocusOverlay | null>(null)
  const commentOverlaysRef = useRef(new Map<string, PositionCommentOverlay>())
  const commentOverlaysByRouteRef = useRef(new Map<string, IndexedPositionCommentOverlay[]>())
  const viewerPositionOverlayRef = useRef<ViewerPositionOverlay | null>(null)
  const viewerPositionHasFitRef = useRef(false)
  const symbolPathRef = useRef<typeof google.maps.SymbolPath | null>(null)
  const overlayViewClassRef = useRef<typeof google.maps.OverlayView | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const liveAnimationActiveRef = useRef(false)
  const autoPanPendingRef = useRef(false)
  const autoPanIdleListenerRef = useRef<google.maps.MapsEventListener | null>(null)
  const lastPositionIndexRef = useRef(-1)
  const previousLivePositionCountRef = useRef(positions.length)
  const currentPositionCountRef = useRef(positions.length)
  const hiddenAtRef = useRef<number | null>(document.hidden ? Date.now() : null)
  const positionCountWhenHiddenRef = useRef(positions.length)
  const catchUpNextRefreshRef = useRef(false)
  const catchUpRefreshTimerRef = useRef<number | null>(null)
  const progressRef = useRef(progress)
  const presentedTimeRef = useRef<number | null>(null)
  const presentedLiveTripRef = useRef(status !== 'ended')
  const initialLiveAnimationStartedRef = useRef(false)
  const lastAnimatedRunRef = useRef(0)
  const previousComparisonModeRef = useRef(comparisonMode)
  const resizeGestureRef = useRef<{ pointerId: number; startY: number; startHeight: number } | null>(null)
  const playerRef = useRef<HTMLDivElement | null>(null)
  const coordinateCopiedTimerRef = useRef<number | null>(null)
  const [failed, setFailed] = useState(false)
  const [mapReady, setMapReady] = useState(false)
  const [animationRun, setAnimationRun] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const [isViewingLive, setIsViewingLive] = useState(status !== 'ended')
  const isViewingLiveRef = useRef(status !== 'ended')
  const [mapHeight, setMapHeight] = useState<number>()
  const [automaticMapHeight, setAutomaticMapHeight] = useState<number>()
  const [positionAgeNow, setPositionAgeNow] = useState(Date.now)
  const [viewerPosition, setViewerPosition] = useState<google.maps.LatLngLiteral | null>(null)
  const [viewerPositionError, setViewerPositionError] = useState<string>()
  const [coordinatesCopied, setCoordinatesCopied] = useState(false)
  const apiKey = import.meta.env.VITE_MAPS_API_KEY
  const hasPositions = positions.length > 0

  useEffect(() => {
    onViewingLiveChange?.(isViewingLive)
  }, [isViewingLive, onViewingLiveChange])

  useEffect(() => () => {
    if (coordinateCopiedTimerRef.current !== null) window.clearTimeout(coordinateCopiedTimerRef.current)
  }, [])

  const routeColor = status === 'ended' ? COMPLETED_ROUTE_COLOR : LIVE_ROUTE_COLOR
  const path = useMemo(
    () => positions.map(({ latitude: lat, longitude: lng }) => ({ lat, lng })),
    [positions],
  )
  const tripTimeline = useMemo(() => createTripTimeline(positions), [positions])
  const comparisonRouteKey = comparisonRoutes.map((route) => {
    const latest = route.positions.at(-1)
    const comments = route.positions.flatMap((position, index) => position.comment
      ? [`${positionCommentKey(route.id, position, index)}:${position.recordedAt}:${position.comment}`]
      : []).join('|')
    return `${route.id}:${route.status}:${route.positions.length}:${latest?.latitude ?? ''}:${latest?.longitude ?? ''}:${route.replayRun}:${comments}`
  }).join('|')
  const positionsRef = useRef(positions)
  const pathRef = useRef(path)
  const focusPositionRef = useRef(path[0])
  positionsRef.current = positions
  currentPositionCountRef.current = positions.length
  pathRef.current = path
  progressRef.current = progress

  const clampMapHeight = (height: number) => {
    const minimum = window.matchMedia('(max-width: 47.99rem)').matches ? 120 : 180
    const maximum = Math.max(360, Math.round(window.innerHeight * 0.8))
    return Math.min(maximum, Math.max(minimum, Math.round(height)))
  }

  useLayoutEffect(() => {
    const wasComparing = previousComparisonModeRef.current
    previousComparisonModeRef.current = comparisonMode
    if (!wasComparing || comparisonMode) return

    setMapHeight(undefined)
    setAutomaticMapHeight(undefined)
    playerRef.current?.closest<HTMLElement>('.account-desktop-detail')?.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto',
    })
  }, [comparisonMode])

  useLayoutEffect(() => {
    if (mapHeight !== undefined) return

    const updateAutomaticHeight = () => {
      const mobile = window.matchMedia('(max-width: 47.99rem)').matches
      if (!mobile && !fitAvailableHeight) {
        setAutomaticMapHeight(undefined)
        return
      }

      const player = playerRef.current
      const canvas = canvasRef.current
      if (!player || !canvas) return

      const canvasBounds = canvas.getBoundingClientRect()
      let nextHeight: number | undefined
      if (fitAvailableHeight && !mobile) {
        const detail = player.closest<HTMLElement>('.account-desktop-detail')
        const sharedTrip = player.closest<HTMLElement>('.shared-trip:not(.shared-trip--embedded)')
        const viewer = player.closest<HTMLElement>('.shared-trip__viewer')
        const availableContainer = detail ?? sharedTrip
        if (availableContainer && viewer) {
          const containerBounds = availableContainer.getBoundingClientRect()
          const viewerBounds = viewer.getBoundingClientRect()
          const contentAfterMap = viewerBounds.bottom - canvasBounds.bottom
          nextHeight = clampMapHeight(containerBounds.bottom - canvasBounds.top - contentAfterMap - 2)
        }
      } else {
        const summary = player.querySelector<HTMLElement>('[data-testid="trip-route-summary"]')
        if (summary) {
          const summaryBounds = summary.getBoundingClientRect()
          const viewportHeight = window.visualViewport?.height ?? window.innerHeight
          const contentAfterMap = summaryBounds.bottom - canvasBounds.bottom
          nextHeight = clampMapHeight(viewportHeight - canvasBounds.top - contentAfterMap - 2)
        }
      }
      if (nextHeight !== undefined) {
        setAutomaticMapHeight((current) => current === nextHeight ? current : nextHeight)
      }
    }

    updateAutomaticHeight()
    const viewer = playerRef.current?.closest<HTMLElement>('.shared-trip__viewer')
    const observedContent = viewer
      ? Array.from(viewer.children).filter((element) => !element.classList.contains('shared-trip__map'))
      : []
    const desktopPanels = playerRef.current?.querySelector<HTMLElement>('.trip-map-player__desktop-panels')
    if (desktopPanels) observedContent.push(desktopPanels)
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateAutomaticHeight)
    observedContent.forEach((element) => observer?.observe(element))
    window.addEventListener('resize', updateAutomaticHeight)
    window.visualViewport?.addEventListener('resize', updateAutomaticHeight)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', updateAutomaticHeight)
      window.visualViewport?.removeEventListener('resize', updateAutomaticHeight)
    }
  }, [comparisonMode, fitAvailableHeight, mapHeight])

  const beginMapResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const currentHeight = canvasRef.current?.getBoundingClientRect().height
    if (!currentHeight) return
    resizeGestureRef.current = { pointerId: event.pointerId, startY: event.clientY, startHeight: currentHeight }
    event.currentTarget.setPointerCapture(event.pointerId)
    event.preventDefault()
  }

  const continueMapResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const gesture = resizeGestureRef.current
    if (!gesture || gesture.pointerId !== event.pointerId) return
    setMapHeight(clampMapHeight(gesture.startHeight + event.clientY - gesture.startY))
  }

  const endMapResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (resizeGestureRef.current?.pointerId !== event.pointerId) return
    resizeGestureRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
  }

  const resizeMapWithKeyboard = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown' && event.key !== 'Home') return
    event.preventDefault()
    if (event.key === 'Home') {
      setMapHeight(undefined)
      return
    }
    const currentHeight = mapHeight ?? canvasRef.current?.getBoundingClientRect().height ?? 0
    setMapHeight(clampMapHeight(currentHeight + (event.key === 'ArrowDown' ? 24 : -24)))
  }

  const stopAnimation = () => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    liveAnimationActiveRef.current = false
    setIsAnimating(false)
  }

  useEffect(() => {
    if (status === 'ended') return

    const clearCatchUpTimer = () => {
      if (catchUpRefreshTimerRef.current === null) return
      window.clearTimeout(catchUpRefreshTimerRef.current)
      catchUpRefreshTimerRef.current = null
    }
    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now()
        positionCountWhenHiddenRef.current = currentPositionCountRef.current
        catchUpNextRefreshRef.current = false
        clearCatchUpTimer()
        return
      }

      const hiddenAt = hiddenAtRef.current
      hiddenAtRef.current = null
      if (!shouldCatchUpAfterTabHidden(hiddenAt, Date.now()) || !isViewingLiveRef.current) return

      if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
      liveAnimationActiveRef.current = false
      progressRef.current = 1
      onProgressChange(1)
      setIsViewingLive(true)
      setIsAnimating(false)

      catchUpNextRefreshRef.current = currentPositionCountRef.current <= positionCountWhenHiddenRef.current
      if (catchUpNextRefreshRef.current) {
        catchUpRefreshTimerRef.current = window.setTimeout(() => {
          catchUpNextRefreshRef.current = false
          catchUpRefreshTimerRef.current = null
        }, LIVE_TAB_REFRESH_GRACE_MS)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearCatchUpTimer()
    }
  }, [onProgressChange, status])

  useLayoutEffect(() => {
    const previousCount = previousLivePositionCountRef.current
    previousLivePositionCountRef.current = positions.length

    if (status === 'ended') {
      liveAnimationActiveRef.current = false
      return
    }
    if (previousCount === 0 || positions.length <= previousCount) return
    if (!isViewingLiveRef.current) {
      const selectedTime = presentedTimeRef.current
      const timeProgress = selectedTime === null ? null : tripTimeline.progressAtTime(selectedTime)
      const fallbackProgress = previousCount > 1 && positions.length > 1
        ? (progressRef.current * (previousCount - 1)) / (positions.length - 1)
        : progressRef.current
      onProgressChange(timeProgress ?? fallbackProgress)
      return
    }
    const continuingLiveAnimation = liveAnimationActiveRef.current

    if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current)
    const presentedTime = presentedTimeRef.current
    const continuingProgress = continuingLiveAnimation && presentedTime !== null
      ? tripTimeline.progressAtTime(presentedTime)
      : null
    const startProgress = continuingProgress ?? tripTimeline.progressAtPositionIndex(previousCount - 1)
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion || startProgress >= 1) {
      liveAnimationActiveRef.current = false
      onProgressChange(1)
      setIsAnimating(false)
      return
    }

    let active = true
    const startedAt = performance.now()
    liveAnimationActiveRef.current = true
    onProgressChange(startProgress)
    setIsAnimating(true)

    const draw = (now: number) => {
      if (!active || !isViewingLiveRef.current) {
        animationFrameRef.current = null
        setIsAnimating(false)
        return
      }
      const fraction = Math.min(1, (now - startedAt) / LIVE_ROUTE_ANIMATION_DURATION_MS)
      onProgressChange(startProgress + (1 - startProgress) * fraction)
      if (fraction < 1) animationFrameRef.current = requestAnimationFrame(draw)
      else {
        animationFrameRef.current = null
        liveAnimationActiveRef.current = false
        isViewingLiveRef.current = true
        setIsViewingLive(true)
        setIsAnimating(false)
      }
    }
    animationFrameRef.current = requestAnimationFrame(draw)

    return () => {
      active = false
      if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }, [onProgressChange, positions.length, status])

  useLayoutEffect(() => {
    presentedTimeRef.current = tripTimeline.timeAt(progress)
  }, [progress, tripTimeline])

  useEffect(() => {
    if (status === 'ended' || !showLivePositionInfo) return
    const timer = window.setInterval(() => setPositionAgeNow(Date.now()), 15_000)
    return () => window.clearInterval(timer)
  }, [showLivePositionInfo, status])

  useEffect(() => {
    if (!showViewerPosition) {
      setViewerPosition(null)
      setViewerPositionError(undefined)
      viewerPositionHasFitRef.current = false
      return
    }
    if (!navigator.geolocation) {
      setViewerPositionError('Your browser cannot provide your position.')
      return
    }

    setViewerPositionError(undefined)
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setViewerPosition({ lat: position.coords.latitude, lng: position.coords.longitude })
        setViewerPositionError(undefined)
      },
      (error) => {
        setViewerPositionError(error.code === error.PERMISSION_DENIED
          ? 'Location permission is needed to show your position.'
          : 'Your position could not be found.')
      },
      { enableHighAccuracy: true, maximumAge: 5_000, timeout: 15_000 },
    )
    return () => navigator.geolocation.clearWatch(watchId)
  }, [showViewerPosition])

  useEffect(() => {
    if (!apiKey || !elementRef.current || positions.length === 0) return

    let active = true
    let initialViewportListener: google.maps.MapsEventListener | null = null
    let mapTypeListener: google.maps.MapsEventListener | null = null
    let viewportListener: google.maps.MapsEventListener | null = null
    configureGoogleMaps(apiKey)

    void Promise.all([
      importLibrary('maps'),
      importLibrary('core'),
    ]).then(([
      { Map, MapTypeControlStyle, MapTypeId, OverlayView, Polyline },
      { ControlPosition, SymbolPath },
    ]) => {
      if (!active || !elementRef.current) return
      const map = new Map(elementRef.current, {
        mapTypeId: readPreferredMapType() === 'satellite' ? MapTypeId.HYBRID : MapTypeId.ROADMAP,
        mapTypeControl: true,
        mapTypeControlOptions: {
          mapTypeIds: [MapTypeId.ROADMAP, MapTypeId.HYBRID],
          position: ControlPosition.TOP_LEFT,
          style: MapTypeControlStyle.HORIZONTAL_BAR,
        },
        streetViewControl: true,
        streetViewControlOptions: {
          position: ControlPosition.RIGHT_CENTER,
        },
        fullscreenControl: true,
        gestureHandling: 'greedy',
      })
      mapTypeListener = map.addListener('maptypeid_changed', () => {
        const selectedType = map.getMapTypeId()
        if (selectedType === MapTypeId.ROADMAP || selectedType === MapTypeId.HYBRID) {
          try {
            storePreferredMapType(selectedType === MapTypeId.HYBRID ? 'satellite' : 'roadmap')
          } catch {
            // The current map selection still works if browser storage is unavailable.
          }
        }
      })
      viewportListener = map.addListener('idle', () => {
        const center = map.getCenter()
        const zoom = map.getZoom()
        if (!center || zoom === undefined) return
        onViewportChange?.({ center: { lat: center.lat(), lng: center.lng() }, zoom })
      })
      mapRef.current = map
      overlayViewClassRef.current = OverlayView
      polylineRef.current = new Polyline({
        map,
        path: [path[0]!],
        strokeColor: routeColor,
        strokeOpacity: 0.92,
        strokeWeight: 5,
      })
      focusOverlayRef.current = createRouteFocusOverlay(OverlayView, map, path[0]!, () => {
        if (!elementRef.current) return
        const mapBounds = map.getBounds()
        const displayedRoute = polylineRef.current?.getPath().getArray() ?? []
        const entireDisplayedRouteIsVisible = mapBounds !== undefined
          && displayedRoute.length > 0
          && displayedRoute.every((point) => mapBounds.contains(point))
        const focusPosition = focusPositionRef.current
        if (entireDisplayedRouteIsVisible && focusPosition) {
          map.panTo(focusPosition)
          map.setZoom(focusedRouteZoom(map.getZoom()))
          return
        }
        fitRouteToViewport(map, elementRef.current, positionsRef.current, pathRef.current)
      })
      lastPositionIndexRef.current = -1
      symbolPathRef.current = SymbolPath
      initialViewportListener = fitRouteToViewport(map, elementRef.current, positions, path)
      setMapReady(true)
    }).catch(() => {
      if (active) setFailed(true)
    })

    return () => {
      active = false
      initialViewportListener?.remove()
      mapTypeListener?.remove()
      viewportListener?.remove()
      polylineRef.current?.setMap(null)
      polylineRef.current = null
      comparisonAnimationFramesRef.current.forEach((frame) => cancelAnimationFrame(frame))
      comparisonAnimationFramesRef.current.clear()
      comparisonPolylinesRef.current.forEach(({ polyline, primary }) => {
        if (!primary) polyline.setMap(null)
      })
      comparisonPolylinesRef.current.clear()
      fittedComparisonIdsRef.current = ''
      focusOverlayRef.current?.remove()
      focusOverlayRef.current = null
      commentOverlaysRef.current.forEach((overlay) => overlay.remove())
      commentOverlaysRef.current.clear()
      commentOverlaysByRouteRef.current.clear()
      viewerPositionOverlayRef.current?.remove()
      viewerPositionOverlayRef.current = null
      viewerPositionHasFitRef.current = false
      autoPanIdleListenerRef.current?.remove()
      autoPanIdleListenerRef.current = null
      autoPanPendingRef.current = false
      mapRef.current = null
      symbolPathRef.current = null
      overlayViewClassRef.current = null
      lastPositionIndexRef.current = -1
      setMapReady(false)
    }
  }, [apiKey, hasPositions, onViewportChange])

  useEffect(() => {
    const map = mapRef.current
    const OverlayView = overlayViewClassRef.current
    if (!mapReady || !map || !OverlayView) return

    const commentedPositions = new Map<string, {
      routeId: string
      index: number
      position: TripPosition
      comment: string
      visible: boolean
    }>()
    const visibleRoutes: Array<{ id: string; positions: TripPosition[]; replayRun?: number }> = comparisonMode
      ? comparisonRoutes
      : [{ id: 'primary', positions }]
    visibleRoutes.forEach((route, routeIndex) => {
      const timeline = createTripTimeline(route.positions)
      const comparisonState = comparisonMode ? comparisonPolylinesRef.current.get(route.id) : undefined
      const replayStarting = comparisonMode
        && comparisonState !== undefined
        && route.replayRun !== undefined
        && route.replayRun > comparisonState.replayRun
      const comparisonProgress = comparisonState?.presentedTime === null
        ? null
        : timeline.progressAtTime(comparisonState?.presentedTime ?? Number.NaN)
      const visiblePositionIndex = comparisonMode
        ? replayStarting
          ? 0
          : comparisonProgress === null
            ? (routeIndex === 0 ? route.positions.length - 1 : 0)
            : timeline.positionSampleAt(comparisonProgress).index
        : timeline.positionSampleAt(progressRef.current).index
      route.positions.forEach((position, index) => {
        const comment = position.comment?.trim()
        if (!comment) return
        commentedPositions.set(positionCommentKey(route.id, position, index), {
          routeId: route.id,
          index,
          position,
          comment,
          visible: index <= visiblePositionIndex,
        })
      })
    })

    commentOverlaysRef.current.forEach((overlay, key) => {
      if (commentedPositions.has(key)) return
      overlay.remove()
      commentOverlaysRef.current.delete(key)
    })
    const overlaysByRoute = new Map<string, IndexedPositionCommentOverlay[]>()
    commentedPositions.forEach(({ routeId, index, position, comment, visible }, key) => {
      const coordinate = { lat: position.latitude, lng: position.longitude }
      let overlay = commentOverlaysRef.current.get(key)
      if (!overlay) {
        overlay = createPositionCommentOverlay(OverlayView, map, coordinate, comment, position.recordedAt)
        commentOverlaysRef.current.set(key, overlay)
      } else {
        overlay.setPosition(coordinate)
        overlay.setContent(comment, position.recordedAt)
      }
      overlay.setVisible(visible)
      const routeOverlays = overlaysByRoute.get(routeId) ?? []
      routeOverlays.push({ index, overlay })
      overlaysByRoute.set(routeId, routeOverlays)
    })
    commentOverlaysByRouteRef.current = overlaysByRoute
  }, [comparisonMode, comparisonRouteKey, mapReady, positions])

  useEffect(() => {
    if (comparisonMode) return
    setRouteCommentVisibility(
      commentOverlaysByRouteRef.current,
      'primary',
      tripTimeline.positionSampleAt(progress).index,
    )
  }, [comparisonMode, progress, tripTimeline])

  useEffect(() => {
    const map = mapRef.current
    if (!mapReady || !map) return

    if (!comparisonMode) {
      const wasComparing = comparisonPolylinesRef.current.size > 0
      comparisonAnimationFramesRef.current.forEach((frame) => cancelAnimationFrame(frame))
      comparisonAnimationFramesRef.current.clear()
      comparisonPolylinesRef.current.forEach(({ polyline, primary }) => {
        if (!primary) polyline.setMap(null)
      })
      comparisonPolylinesRef.current.clear()
      fittedComparisonIdsRef.current = ''
      polylineRef.current?.setOptions({ strokeColor: routeColor, strokeOpacity: .92 })
      if (wasComparing && elementRef.current) fitRouteToViewport(map, elementRef.current, positions, path)
      return
    }
    if (catchUpNextRefreshRef.current) {
      catchUpNextRefreshRef.current = false
      if (catchUpRefreshTimerRef.current !== null) window.clearTimeout(catchUpRefreshTimerRef.current)
      catchUpRefreshTimerRef.current = null
      if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
      liveAnimationActiveRef.current = false
      progressRef.current = 1
      onProgressChange(1)
      setIsViewingLive(true)
      setIsAnimating(false)
      return
    }

    const visibleIds = new Set(comparisonRoutes.map((route) => route.id))
    comparisonPolylinesRef.current.forEach(({ polyline, primary }, tripId) => {
      if (visibleIds.has(tripId)) return
      const frame = comparisonAnimationFramesRef.current.get(tripId)
      if (frame !== undefined) cancelAnimationFrame(frame)
      comparisonAnimationFramesRef.current.delete(tripId)
      if (!primary) polyline.setMap(null)
      comparisonPolylinesRef.current.delete(tripId)
    })

    const bounds = new google.maps.LatLngBounds()
    let pointCount = 0
    comparisonRoutes.forEach((comparisonRoute, routeIndex) => {
      const comparisonPath = comparisonRoute.positions.map(({ latitude: lat, longitude: lng }) => ({ lat, lng }))
      if (!comparisonPath.length) return
      comparisonPath.forEach((point) => bounds.extend(point))
      pointCount += comparisonPath.length
      let existing = comparisonPolylinesRef.current.get(comparisonRoute.id)
      const primary = routeIndex === 0
      if (existing && existing.primary !== primary) {
        const frame = comparisonAnimationFramesRef.current.get(comparisonRoute.id)
        if (frame !== undefined) cancelAnimationFrame(frame)
        comparisonAnimationFramesRef.current.delete(comparisonRoute.id)
        if (!existing.primary) existing.polyline.setMap(null)
        comparisonPolylinesRef.current.delete(comparisonRoute.id)
        existing = undefined
      }
      if (!existing) {
        const polyline = primary
          ? polylineRef.current
          : new google.maps.Polyline({ map, path: [comparisonPath[0]!], zIndex: routeIndex + 1 })
        if (!polyline) return
        const positionCount = primary ? comparisonRoute.positions.length : 1
        existing = {
          polyline,
          positionCount,
          targetPositionCount: positionCount,
          presentedTime: primary ? createTripTimeline(comparisonRoute.positions).timeAt(1) : null,
          replayRun: comparisonRoute.replayRun,
          primary,
        }
        comparisonPolylinesRef.current.set(comparisonRoute.id, existing)
      }

      existing.polyline.setOptions({
        strokeColor: comparisonRoute.color,
        strokeOpacity: .92,
        strokeWeight: 5,
        zIndex: routeIndex + 1,
      })

      const isNewExtraRoute = !primary && existing.positionCount <= 1
      const receivedLivePositions = comparisonRoute.status !== 'ended'
        && comparisonRoute.positions.length > existing.targetPositionCount
      const replayRequested = comparisonRoute.replayRun > existing.replayRun
      const activeFrame = comparisonAnimationFramesRef.current.get(comparisonRoute.id)
      if (activeFrame !== undefined && !receivedLivePositions && !replayRequested) return
      if (activeFrame !== undefined) {
        cancelAnimationFrame(activeFrame)
        comparisonAnimationFramesRef.current.delete(comparisonRoute.id)
      }
      if (!isNewExtraRoute && !receivedLivePositions && !replayRequested) {
        existing.polyline.setPath(comparisonPath)
        existing.polyline.setOptions({
          icons: routeMarkerIcons(symbolPathRef.current!, comparisonRoute.status, comparisonRoute.color, comparisonRoute.color, true),
        })
        existing.positionCount = comparisonRoute.positions.length
        existing.targetPositionCount = comparisonRoute.positions.length
        existing.presentedTime = createTripTimeline(comparisonRoute.positions).timeAt(1)
        existing.replayRun = comparisonRoute.replayRun
        setRouteCommentVisibility(commentOverlaysByRouteRef.current, comparisonRoute.id, comparisonRoute.positions.length - 1)
        return
      }

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const comparisonTimeline = createTripTimeline(comparisonRoute.positions)
      const startProgress = isNewExtraRoute || replayRequested
        ? 0
        : (existing.presentedTime === null ? null : comparisonTimeline.progressAtTime(existing.presentedTime))
          ?? comparisonTimeline.progressAtPositionIndex(Math.max(0, existing.positionCount - 1))
      if (reduceMotion || startProgress >= .999) {
        existing.polyline.setPath(comparisonPath)
        existing.polyline.setOptions({
          icons: routeMarkerIcons(symbolPathRef.current!, comparisonRoute.status, comparisonRoute.color, comparisonRoute.color, true),
        })
        existing.positionCount = comparisonRoute.positions.length
        existing.targetPositionCount = comparisonRoute.positions.length
        existing.presentedTime = comparisonTimeline.timeAt(1)
        existing.replayRun = comparisonRoute.replayRun
        setRouteCommentVisibility(commentOverlaysByRouteRef.current, comparisonRoute.id, comparisonRoute.positions.length - 1)
        return
      }

      const startedAt = performance.now()
      const duration = isNewExtraRoute || replayRequested ? ROUTE_ANIMATION_DURATION_MS : LIVE_ROUTE_ANIMATION_DURATION_MS
      existing.targetPositionCount = comparisonRoute.positions.length
      existing.presentedTime = comparisonTimeline.timeAt(startProgress)
      existing.replayRun = comparisonRoute.replayRun
      setRouteCommentVisibility(
        commentOverlaysByRouteRef.current,
        comparisonRoute.id,
        comparisonTimeline.positionSampleAt(startProgress).index,
      )
      const draw = (now: number) => {
        const fraction = Math.min(1, (now - startedAt) / duration)
        const nextProgress = startProgress + (1 - startProgress) * fraction
        existing!.presentedTime = comparisonTimeline.timeAt(nextProgress)
        setRouteCommentVisibility(
          commentOverlaysByRouteRef.current,
          comparisonRoute.id,
          comparisonTimeline.positionSampleAt(nextProgress).index,
        )
        existing!.polyline.setPath(routePathAtProgress(comparisonRoute.positions, nextProgress))
        existing!.polyline.setOptions({
          icons: routeMarkerIcons(
            symbolPathRef.current!,
            comparisonRoute.status,
            comparisonRoute.color,
            comparisonRoute.color,
            nextProgress >= .999,
          ),
        })
        if (nextProgress < 1) {
          comparisonAnimationFramesRef.current.set(comparisonRoute.id, requestAnimationFrame(draw))
        } else {
          comparisonAnimationFramesRef.current.delete(comparisonRoute.id)
          existing!.positionCount = comparisonRoute.positions.length
          existing!.targetPositionCount = comparisonRoute.positions.length
        }
      }
      comparisonAnimationFramesRef.current.set(comparisonRoute.id, requestAnimationFrame(draw))
    })

    const comparisonIds = comparisonRoutes.map((route) => route.id).join('|')
    if (pointCount && comparisonIds !== fittedComparisonIdsRef.current) {
      fittedComparisonIdsRef.current = comparisonIds
      const listener = map.addListener('idle', () => {
        listener.remove()
        const zoom = map.getZoom()
        if (zoom !== undefined && zoom > 17) map.setZoom(17)
      })
      map.fitBounds(bounds, 56)
    }
  }, [comparisonMode, comparisonRouteKey, mapReady, path, positions, routeColor])

  useEffect(() => {
    const map = mapRef.current
    const target = path.at(-1)
    if (!showViewerPosition || !mapReady || !map || !viewerPosition || !target) {
      viewerPositionOverlayRef.current?.remove()
      viewerPositionOverlayRef.current = null
      viewerPositionHasFitRef.current = false
      return
    }

    if (!viewerPositionOverlayRef.current) {
      viewerPositionOverlayRef.current = createViewerPositionOverlay(
        google.maps.OverlayView,
        map,
        viewerPosition,
      )
    }
    viewerPositionOverlayRef.current.setPosition(viewerPosition)
    const distance = distanceBetweenCoordinates(
      { latitude: viewerPosition.lat, longitude: viewerPosition.lng },
      { latitude: target.lat, longitude: target.lng },
    )
    viewerPositionOverlayRef.current.setInfo(`${formatViewerDistance(distance, unitSystem)} from traveller`)

    if (!viewerPositionHasFitRef.current) {
      const bounds = new google.maps.LatLngBounds()
      bounds.extend(viewerPosition)
      bounds.extend(target)
      map.fitBounds(bounds, 60)
      viewerPositionHasFitRef.current = true
    }
  }, [mapReady, path, showViewerPosition, unitSystem, viewerPosition])

  useEffect(() => {
    const canAnimate = !apiKey || failed || mapReady
    if (positions.length === 0 || !canAnimate) return
    const replayRequested = animationRun > lastAnimatedRunRef.current
    if (status !== 'ended' && !replayRequested) {
      presentedLiveTripRef.current = true
      if (status !== 'running' || initialLiveAnimationStartedRef.current) {
        if (!initialLiveAnimationStartedRef.current) onProgressChange(1)
        setIsAnimating(false)
        return
      }
      initialLiveAnimationStartedRef.current = true
    }
    if (status === 'ended' && presentedLiveTripRef.current && !replayRequested) {
      presentedLiveTripRef.current = false
      onProgressChange(1)
      setIsAnimating(false)
      return
    }
    lastAnimatedRunRef.current = animationRun
    const initialLiveAnimation = status === 'running' && !replayRequested
    liveAnimationActiveRef.current = initialLiveAnimation
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion) {
      onProgressChange(1)
      if (status !== 'ended') {
        isViewingLiveRef.current = true
        setIsViewingLive(true)
      }
      setIsAnimating(false)
      return
    }

    let active = true
    const startedAt = performance.now()
    const startProgress = 0
    const animationDuration = ROUTE_ANIMATION_DURATION_MS
    onProgressChange(startProgress)
    setIsAnimating(true)

    const draw = (now: number) => {
      if (!active || (initialLiveAnimation && !isViewingLiveRef.current)) return
      const fraction = Math.min(1, (now - startedAt) / animationDuration)
      const nextProgress = startProgress + (1 - startProgress) * fraction
      onProgressChange(nextProgress)
      if (nextProgress < 1) animationFrameRef.current = requestAnimationFrame(draw)
      else {
        animationFrameRef.current = null
        liveAnimationActiveRef.current = false
        if (status !== 'ended') {
          isViewingLiveRef.current = true
          setIsViewingLive(true)
        }
        setIsAnimating(false)
      }
    }
    animationFrameRef.current = requestAnimationFrame(draw)

    return () => {
      active = false
      if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }, [animationRun, apiKey, failed, mapReady, onProgressChange, status])

  useEffect(() => {
    if (comparisonMode || !mapReady || !polylineRef.current || !symbolPathRef.current) return
    const positionSample = tripTimeline.positionSampleAt(progress)
    const positionIndex = positionSample.index
    if (positionIndex < 0) return
    const previousPositionIndex = lastPositionIndexRef.current
    lastPositionIndexRef.current = positionIndex
    const displayedPath = polylineRef.current.getPath()
    const basePathLength = positionIndex + 1
    if (positionIndex !== previousPositionIndex || displayedPath.getLength() < basePathLength) {
      polylineRef.current.setPath(path.slice(0, basePathLength))
    }
    const mutablePath = polylineRef.current.getPath()
    let latestPoint = path[positionIndex]
    if (positionSample.nextIndex !== null && positionSample.fraction > 0) {
      const from = path[positionIndex]!
      const to = path[positionSample.nextIndex]!
      latestPoint = {
        lat: from.lat + (to.lat - from.lat) * positionSample.fraction,
        lng: from.lng + (to.lng - from.lng) * positionSample.fraction,
      }
      const animatedPoint = new google.maps.LatLng(latestPoint)
      if (mutablePath.getLength() === basePathLength) mutablePath.push(animatedPoint)
      else if (mutablePath.getLength() === basePathLength + 1) mutablePath.setAt(basePathLength, animatedPoint)
      else polylineRef.current.setPath([...path.slice(0, basePathLength), latestPoint])
    } else {
      while (mutablePath.getLength() > basePathLength) mutablePath.removeAt(mutablePath.getLength() - 1)
    }
    polylineRef.current.setOptions({
      strokeColor: routeColor,
      icons: routeMarkerIcons(symbolPathRef.current, status, routeColor, routeColor, status === 'ended' && progress >= .999),
    })
    if (latestPoint) {
      focusPositionRef.current = latestPoint
      focusOverlayRef.current?.setPosition(latestPoint)
    }
    const metrics = tripPointMetricsAt(positions, positionIndex)
    const speed = metrics.speedKilometersPerHour === undefined
      ? 'Speed unavailable'
      : formatSpeed(metrics.speedKilometersPerHour, unitSystem)
    focusOverlayRef.current?.setInfo(
      status !== 'ended' && showLivePositionInfo
        ? `${speed} · ${formatPositionAge(tripTimeline.timeAt(progress), positionAgeNow)}`
        : '',
    )
    const map = mapRef.current
    const visibleBounds = map?.getBounds()
    if (
      latestPoint
      && map
      && visibleBounds
      && followLatestPosition
      && status !== 'ended'
      && (progress >= 0.999 || liveAnimationActiveRef.current)
      && previousPositionIndex >= 0
      && (positionIndex > previousPositionIndex || liveAnimationActiveRef.current)
      && !visibleBounds.contains(latestPoint)
      && !autoPanPendingRef.current
    ) {
      autoPanPendingRef.current = true
      autoPanIdleListenerRef.current?.remove()
      const listener = map.addListener('idle', () => {
        listener.remove()
        autoPanIdleListenerRef.current = null
        autoPanPendingRef.current = false
      })
      autoPanIdleListenerRef.current = listener
      map.panTo(latestPoint)
    }
  }, [comparisonMode, followLatestPosition, mapReady, path, positionAgeNow, positions, progress, routeColor, showLivePositionInfo, status, tripTimeline, unitSystem])

  const viewingHistory = status !== 'ended' && !isViewingLive
  const timelineDisplayProgress = status !== 'ended' && isViewingLive ? 1 : progress
  const currentClock = formatClock(tripTimeline.timeAt(timelineDisplayProgress))
  const elapsed = formatElapsed(tripTimeline.elapsedSecondsAt(timelineDisplayProgress))
  const currentPositionSample = tripTimeline.positionSampleAt(timelineDisplayProgress)
  const currentPositionIndex = currentPositionSample.index
  const pointMetrics = tripPointMetricsAt(positions, currentPositionIndex)
  const currentCoordinate = (() => {
    const from = positions[currentPositionSample.index]
    if (!from) return null
    const to = currentPositionSample.nextIndex === null ? undefined : positions[currentPositionSample.nextIndex]
    if (!to || currentPositionSample.fraction <= 0) {
      return { latitude: from.latitude, longitude: from.longitude }
    }
    return {
      latitude: from.latitude + (to.latitude - from.latitude) * currentPositionSample.fraction,
      longitude: from.longitude + (to.longitude - from.longitude) * currentPositionSample.fraction,
    }
  })()
  const coordinateText = currentCoordinate
    ? `${currentCoordinate.latitude.toFixed(6)}, ${currentCoordinate.longitude.toFixed(6)}`
    : ''

  const copyCoordinates = async () => {
    if (!coordinateText) return
    try {
      await navigator.clipboard.writeText(coordinateText)
      setCoordinatesCopied(true)
      if (coordinateCopiedTimerRef.current !== null) window.clearTimeout(coordinateCopiedTimerRef.current)
      coordinateCopiedTimerRef.current = window.setTimeout(() => {
        coordinateCopiedTimerRef.current = null
        setCoordinatesCopied(false)
      }, 1_500)
    } catch {
      setCoordinatesCopied(false)
    }
  }

  const goLive = () => {
    stopAnimation()
    isViewingLiveRef.current = true
    setIsViewingLive(true)
    onProgressChange(1)
  }

  const beginTimelineReview = () => {
    if (status === 'ended') return
    stopAnimation()
    isViewingLiveRef.current = false
    setIsViewingLive(false)
  }

  const timelineControls = positions.length > 0 ? (
    <>
      <div className="trip-timeline__heading">
        <div className="trip-timeline__heading-copy">
          <span className={viewingHistory ? 'trip-timeline__eyebrow trip-timeline__eyebrow--history' : 'trip-timeline__eyebrow'} role={viewingHistory ? 'status' : undefined}>
            {viewingHistory ? <>Viewing <span>history</span></> : <>Trip <span>timeline</span></>}
          </span>
          <output className="trip-timeline__time" htmlFor="trip-progress">
            {currentClock} <small>+{elapsed}</small>
          </output>
        </div>
        <div className="trip-timeline__actions">
          {viewingHistory ? (
            <button className="trip-timeline__go-live" type="button" title="Return to the latest live position" onClick={goLive}>
              <span aria-hidden="true" /> Go live
            </button>
          ) : null}
        </div>
      </div>
      <div className="trip-timeline__scrubber">
        <div className="trip-timeline__track">
          <input
            id="trip-progress"
            className="trip-timeline__range"
            type="range"
            min="0"
            max="1000"
            step="1"
            value={Math.round(timelineDisplayProgress * 1000)}
            onPointerDown={beginTimelineReview}
            onKeyDown={(event) => {
              if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) {
                beginTimelineReview()
              }
            }}
            onChange={(event) => {
              stopAnimation()
              const nextProgress = Number(event.currentTarget.value) / 1000
              if (status !== 'ended') {
                const atLiveEdge = nextProgress >= 0.999
                isViewingLiveRef.current = atLiveEdge
                setIsViewingLive(atLiveEdge)
                onProgressChange(atLiveEdge ? 1 : nextProgress)
                return
              }
              onProgressChange(nextProgress)
            }}
            aria-label="Trip time"
            aria-valuetext={`${currentClock}, ${elapsed} from start${viewingHistory ? ', viewing history' : ''}`}
            style={{ '--trip-progress': `${timelineDisplayProgress * 100}%` } as CSSProperties}
          />
          <div className="trip-timeline__limits" aria-hidden="true">
            <span>{formatClock(tripTimeline.timeAt(0))}</span>
            <span>{formatClock(tripTimeline.timeAt(1))}</span>
          </div>
        </div>
        <button
          className="trip-timeline__replay"
          type="button"
          onClick={() => {
            if (status !== 'ended') {
              isViewingLiveRef.current = false
              setIsViewingLive(false)
            }
            setAnimationRun((value) => value + 1)
          }}
          aria-label={isAnimating ? "Restart trip replay" : "Replay trip"}
          title={isAnimating ? "Restart trip replay" : "Replay trip"}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 8V4m0 0h4M5 4l3 3a7 7 0 1 1-1.5 7" /></svg>
        </button>
      </div>
      <section className="trip-timeline__point-info" aria-labelledby="trip-point-heading">
        <div className="trip-timeline__point-heading">
          <h3 id="trip-point-heading">At this point</h3>
          <span role="status" aria-live="polite">{coordinatesCopied ? 'Coordinates copied' : ''}</span>
        </div>
        <dl className="trip-timeline__point-metrics">
          <div>
            <dt>Distance</dt>
            <dd data-testid="timeline-distance">{formatDistance(pointMetrics.distanceKilometers, unitSystem)}</dd>
          </div>
          <div>
            <dt>Speed</dt>
            <dd data-testid="timeline-speed">
              {pointMetrics.speedKilometersPerHour === undefined
                ? '–'
                : formatSpeed(pointMetrics.speedKilometersPerHour, unitSystem)}
            </dd>
          </div>
          <div>
            <dt>Latitude</dt>
            <dd><button className="trip-timeline__coordinate" type="button" onClick={copyCoordinates} title="Copy latitude and longitude">{currentCoordinate?.latitude.toFixed(6) ?? '–'}</button></dd>
          </div>
          <div className="trip-timeline__longitude">
            <button className="trip-timeline__copy-coordinates" type="button" onClick={copyCoordinates} aria-label="Copy latitude and longitude" title="Copy latitude and longitude">
              <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="8" y="8" width="11" height="11" rx="2" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></svg>
            </button>
            <dt>Longitude</dt>
            <dd><button className="trip-timeline__coordinate" type="button" onClick={copyCoordinates} title="Copy latitude and longitude">{currentCoordinate?.longitude.toFixed(6) ?? '–'}</button></dd>
          </div>
        </dl>
      </section>
    </>
  ) : null

  const timeline = timelineControls ? (
    <div className="trip-timeline" data-testid="trip-timeline">
      {timelineInfoSlot}
      {timelineControls}
    </div>
  ) : null

  return (
    <div
      ref={playerRef}
      className={`trip-map-player${comparisonMode ? ' trip-map-player--comparison' : ''}`}
      data-testid="trip-map-player"
      data-playback-progress={Math.round(progress * 1000)}
      data-comparison-mode={comparisonMode ? 'true' : undefined}
    >
      <div
        ref={canvasRef}
        className="trip-map-player__canvas"
        style={comparisonMode || (mapHeight === undefined && automaticMapHeight === undefined)
          ? undefined
          : { height: `${mapHeight ?? automaticMapHeight}px` }}
      >
        {!apiKey || failed || positions.length === 0 ? (
          <div className="map-fallback" role="img" aria-label="Trip route preview">
            <span>{failed ? 'The map could not be loaded' : 'Add a Google Maps key to enable the map'}</span>
          </div>
        ) : (
          <div ref={elementRef} className="google-trip-map" aria-label="Trip route on map" />
        )}
        {showViewerPosition && viewerPositionError ? (
          <p className="trip-map-geolocation-message" role="status">{viewerPositionError}</p>
        ) : null}
        {comparisonOverlaySlot}
      </div>
      {!comparisonMode ? <><button
        className="trip-map-resizer"
        type="button"
        aria-label="Resize map height"
        title="Drag to resize the map. Press Home to reset."
        onPointerDown={beginMapResize}
        onPointerMove={continueMapResize}
        onPointerUp={endMapResize}
        onPointerCancel={endMapResize}
        onDoubleClick={() => setMapHeight(undefined)}
        onKeyDown={resizeMapWithKeyboard}
      >
        <span aria-hidden="true" />
      </button>
      {timelineSideSlot ? (
        <div className="trip-map-player__desktop-panels">
          <div className="trip-timeline trip-map-player__desktop-summary">{timelineInfoSlot}</div>
          <div className="trip-timeline trip-map-player__desktop-timeline" data-testid="trip-timeline">{timelineControls}</div>
          <div className="trip-map-player__timeline-side">{timelineSideSlot}</div>
        </div>
      ) : timeline}</> : null}
    </div>
  )
}
